import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PhoneCall, Mail, MapPin, Send, CheckCircle2, Loader2, Building2 } from "lucide-react";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-brand/20 bg-paper px-4 py-3 text-sm text-ink placeholder:text-moss/60 focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition-all";

export default function Contact() {
  const [params] = useSearchParams();
  const prefillBill = params.get("bill");
  const prefillKw = params.get("kw");

  const [form, setForm] = useState({
    name: "", phone: "", email: "", city: "",
    bill_range: prefillBill ? `₹${Number(prefillBill).toLocaleString("en-IN")} / month (calculator)` : "",
    property_type: "",
    message: prefillKw ? `Calculator estimate: ${prefillKw} kW system. Please send an exact quote.` : "",
  });
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please fill your name and phone number.");
      return;
    }
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, { ...form, source: prefillBill ? "calculator" : "contact" });
      setStatus("done");
      toast.success("Request received! Our solar expert will call you within 24 hours.");
    } catch (err) {
      setStatus("idle");
      toast.error("Something went wrong. Please call us directly.");
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative bg-brand-deep text-white grain overflow-hidden pt-32 pb-20">
        <div className="absolute -bottom-40 -right-32 w-[26rem] h-[26rem] rounded-full bg-gold/10 blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-mono text-xs tracking-[0.35em] text-gold mb-4"
          >
            CONTACT US
          </motion.p>
          <div className="overflow-hidden">
            <motion.h1
              initial={{ y: "100%" }} animate={{ y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]"
            >
              Let's talk about <span className="text-gold">your roof.</span>
            </motion.h1>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.7 }}
            className="mt-6 max-w-2xl text-white/70 text-base md:text-lg"
          >
            Book a free rooftop survey or ask anything — a solar engineer responds within 24 hours.
          </motion.p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid lg:grid-cols-5 gap-8">
          <Reveal className="lg:col-span-3">
            <div className="rounded-[2rem] bg-white border border-brand/10 p-7 md:p-10 shadow-[0_16px_50px_rgba(3,59,43,0.08)]">
              {status === "done" ? (
                <div className="py-14 text-center" data-testid="contact-success-state">
                  <CheckCircle2 size={60} className="mx-auto text-brand" strokeWidth={1.5} />
                  <h2 className="mt-5 font-display font-extrabold text-3xl text-ink">Shukriya, {form.name.split(" ")[0]}!</h2>
                  <p className="mt-3 text-moss max-w-sm mx-auto">
                    Your request is with our team. Expect a call from <span className="font-bold text-brand">+91 96196 13720</span> within 24 hours to schedule your free survey.
                  </p>
                  <button
                    onClick={() => { setStatus("idle"); setForm({ name: "", phone: "", email: "", city: "", bill_range: "", property_type: "", message: "" }); }}
                    data-testid="contact-send-another-button"
                    className="mt-7 rounded-full border border-brand/25 text-brand px-6 py-2.5 text-sm font-bold hover:bg-brand hover:text-white transition-colors"
                  >
                    Send another request
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                  <h2 className="font-display font-bold text-2xl text-ink">Book Free Rooftop Survey</h2>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold tracking-wide text-moss mb-1.5 uppercase">Full Name *</label>
                      <input data-testid="contact-form-name" className={inputCls} placeholder="e.g. Rahul Sharma" value={form.name} onChange={set("name")} required />
                    </div>
                    <div>
                      <label className="block text-xs font-bold tracking-wide text-moss mb-1.5 uppercase">Phone Number *</label>
                      <input data-testid="contact-form-phone" type="tel" className={inputCls} placeholder="10-digit mobile" value={form.phone} onChange={set("phone")} required />
                    </div>
                    <div>
                      <label className="block text-xs font-bold tracking-wide text-moss mb-1.5 uppercase">Email</label>
                      <input data-testid="contact-form-email" type="email" className={inputCls} placeholder="you@example.com" value={form.email} onChange={set("email")} />
                    </div>
                    <div>
                      <label className="block text-xs font-bold tracking-wide text-moss mb-1.5 uppercase">City / PIN Code</label>
                      <input data-testid="contact-form-city" className={inputCls} placeholder="e.g. Bengaluru 560095" value={form.city} onChange={set("city")} />
                    </div>
                    <div>
                      <label className="block text-xs font-bold tracking-wide text-moss mb-1.5 uppercase">Monthly Bill Range</label>
                      <select data-testid="contact-form-bill-range" className={inputCls} value={form.bill_range} onChange={set("bill_range")}>
                        <option value="">Select range</option>
                        <option>Below ₹2,000</option>
                        <option>₹2,000 – ₹5,000</option>
                        <option>₹5,000 – ₹10,000</option>
                        <option>Above ₹10,000</option>
                        {prefillBill && <option>{form.bill_range}</option>}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold tracking-wide text-moss mb-1.5 uppercase">Property Type</label>
                      <select data-testid="contact-form-property-type" className={inputCls} value={form.property_type} onChange={set("property_type")}>
                        <option value="">Select type</option>
                        <option>Independent House / Villa</option>
                        <option>Apartment (own terrace rights)</option>
                        <option>Commercial / Office</option>
                        <option>Factory / Warehouse</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold tracking-wide text-moss mb-1.5 uppercase">Message</label>
                    <textarea data-testid="contact-form-message" rows={3} className={inputCls} placeholder="Anything we should know? (shadows, lift access, timing...)" value={form.message} onChange={set("message")} />
                  </div>
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    data-testid="contact-form-submit-button"
                    className="w-full flex items-center justify-center gap-2 rounded-full bg-brand text-white py-4 font-bold hover:scale-[1.01] transition-transform disabled:opacity-60"
                  >
                    {status === "sending" ? <Loader2 size={18} className="animate-spin" /> : <Send size={17} />}
                    {status === "sending" ? "Sending..." : "Request Free Survey"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>

          <div className="lg:col-span-2 space-y-6">
            <Reveal delay={0.1}>
              <div className="rounded-[2rem] bg-brand-deep text-white p-8 grain relative overflow-hidden" data-testid="contact-info-card">
                <div className="relative z-10 space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-2xl bg-gold flex items-center justify-center shrink-0">
                      <PhoneCall size={19} className="text-brand-deep" />
                    </div>
                    <div>
                      <p className="font-mono text-[10px] tracking-[0.3em] text-gold">CALL US</p>
                      <a href="tel:+919619613720" data-testid="contact-info-phone" className="font-display font-bold text-xl hover:text-gold transition-colors">+91 96196 13720</a>
                      <p className="text-xs text-white/50 mt-0.5">Mon–Sat, 9 AM – 7 PM</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-2xl bg-gold flex items-center justify-center shrink-0">
                      <Mail size={19} className="text-brand-deep" />
                    </div>
                    <div>
                      <p className="font-mono text-[10px] tracking-[0.3em] text-gold">EMAIL</p>
                      <a href="mailto:info@solarsoch.com" data-testid="contact-info-email" className="font-display font-bold text-lg hover:text-gold transition-colors break-all">info@solarsoch.com</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-2xl bg-gold flex items-center justify-center shrink-0">
                      <MapPin size={19} className="text-brand-deep" />
                    </div>
                    <div>
                      <p className="font-mono text-[10px] tracking-[0.3em] text-gold">VISIT US</p>
                      <p className="text-sm text-white/80 leading-relaxed">
                        #404D, 1st Floor, KHB Colony, 5th Block,<br />
                        Koramangala, Bengaluru – 560095
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-2xl bg-gold flex items-center justify-center shrink-0">
                      <Building2 size={19} className="text-brand-deep" />
                    </div>
                    <div>
                      <p className="font-mono text-[10px] tracking-[0.3em] text-gold">REGISTERED AS</p>
                      <p className="text-sm text-white/80">Thrivasol Renewable Technologies Private Limited</p>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.18}>
              <div className="rounded-[2rem] overflow-hidden border border-brand/10 h-72" data-testid="contact-map">
                <iframe
                  title="SolarSoch location — Koramangala, Bengaluru"
                  src="https://maps.google.com/maps?q=KHB%20Colony%2C%205th%20Block%2C%20Koramangala%2C%20Bengaluru%20560095&t=&z=14&ie=UTF8&iwloc=&output=embed"
                  className="w-full h-full border-0"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
